<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'

definePageMeta({
    layout: 'plain',
    middleware: ['home-landing-page'],
    landingPage: true,
    heroVideo: true,
})

defineOptions({
    prefetch: (to) => {
        usePageData('frontpage')
        useSignupPromotions({ query: to.query, server: true, lazy: false })
    },
})

const { site } = useCoreConfig()
const { isFromPartner } = usePartner()
const { authOpen } = useLandingPageAuth()
useLandingTracking()
// Fetches the offers during SSR; StickyTerms' own call is browser-only.
useSignupPromotions({ server: true, lazy: false })

// Hydrate the content below the fold (and load its deferred images) on the visitor's first
// scroll, or once the page has loaded and the cookie banner has been answered, whichever comes
// first. Neither happens in a lab run, so the section costs nothing there, as with
// hydrate-on-visible, while a visitor who scrolls or answers finds it ready.
const scrolled = ref(false)
useEventListener('scroll', () => {
    scrolled.value = true
}, { once: true, passive: true })
const { isConsentResolved: consentAnswered } = useCookieConsent()
const pageLoaded = usePageLoaded()
const hydrateContent = computed(() => scrolled.value || (pageLoaded.value && consentAnswered.value))
const { data: pageData } = await usePageData('frontpage')

const { getHead, getSeoMeta } = useContentSeo(
    pageData.value?.seo || {
        metaTitle: `Online Casino | Spil sjove spilleautomater hos ${site.title}`,
        metaDescription: `Bliv medlem af ${site.title} i dag. Nye spillere får 100% i velkomstbonus. Regler & vilkår. 18+. Spil ansvarligt.`,
    }
)

useHead(getHead())
useSeoMeta(getSeoMeta())
</script>

<template>
    <div class="landing-page" :class="{ 'landing-page--auth-open': authOpen }">
        <LandingPageNavBar fallback-path="/lobby" />

        <main
            class="landing-page__main"
            data-test="welcome-page"
        >
            <LandingPageHeader />

            <StickyTerms
                :title="isFromPartner ? 'Bonus vilkår' : 'Vigtige Betingelser'"
                is-landing-page
                reset-margin
                enable-multiple
                :sticky-config="{ container: '.landing-page', minHeight: 48, offset: 50, backgroundColor: '#0d1a2b' }"
            />

            <div
                v-if="!isEmpty(pageData?.dynamicContent)"
                data-lp-section="content"
            >
                <LazyIfNimbusContentDynamicRenderer
                    :content="pageData?.dynamicContent"
                    :max-width="1024"
                    color="#fff"
                    :hydrate-when="hydrateContent"
                    defer-media
                />
            </div>

            <section class="bonus-terms" data-lp-section="terms">
                <StickyTerms
                    title="Bonus vilkår"
                    :show-title="true"
                    is-landing-page
                    force-full-terms
                    enable-multiple
                />
            </section>
        </main>
        <div class="landing-page__footer" data-lp-section="footer">
            <LazyVisibleFooterContainer :enable-terms="false" is-landing-page hydrate-on-visible />
        </div>
    </div>
</template>

<style scoped lang="scss">
$body-bg-light: #1181c9;
$body-bg-dark: #0a5f8a;
// Light body — spilnu-light brand tokens (#E9EAEE background, white surfaces,
// #333 text). The video hero + trust-section above live in the header
// (LandingPageHeader) and stay dark; only the sections below the hero
// are themed light here.
.landing-page {
    --theme-background: var(--background, #{$body-bg});
    // The sticky terms band pins under the dark hero and keeps the header's
    // dark styling, so its text stays light. The light body re-scopes this
    // dark in .bonus-terms below.
    --landing-page-bg: #{$body-bg-light};
    --sticky-terms-background: #1181c9;
    --login-button-color: #fff;
    --sticky-terms-color: #fff;

    background-color: var(--landing-page-bg);

    .theme-provider {
        --background: var(--landing-page-bg);
    }

    &__main {
        a:not(.btn--cta) {
            color: inherit;
        }
    }
    &__footer {
        padding-top: 50px;
        background-color: var(--theme-background, $body-bg);
    }
}

.dynamic-content {
    :deep(.winners-sidebar) {
        --game-winner-bg: initial;
        --game-winner-color: initial;
        --game-winner-bingo-bg: initial;
        --game-winner-bingo-color: initial;
        --game-winner-big-win-bg: initial;
        --game-winner-big-win-color: initial;
    }

    > :deep(div:has(.usp-bar, .winners-sidebar)) {
        padding-bottom: 0 !important;
    }

    > :deep(div) {
        padding-left: 15px;
        padding-right: 15px;
    }

    :deep(.media-text__content) {
        text-align: center;

        @include tablet(md) {
            text-align: left;
        }
    }

    :deep(.text) {
        max-width: 800px;
    }

    :deep(.blog-posts) {
        max-width: 800px;
    }

    :deep(.media-text__text) {
        a {
            color: inherit;
            text-decoration: underline;
        }
    }
    :deep(.media-text__title) {
        a {
            color: inherit;
        }
    }
}

// The sticky-terms seam treatment lives in the header component
// (LandingPage/Header.vue), scoped to .landing-page via :global().

:global(.bonus-terms .offer-terms) {
    --sticky-terms-color: var(--on-surface, #fff);
    --on-background: #fff;
    --on-surface: #fff;

    margin-top: 30px;
    color: var(--on-background, #fff);
    background-color: var(--landing-page-bg);
    width: 100%;
    max-width: 1024px;
}

.footer {
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 2rem 2rem 2rem;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
    }
}
</style>
